import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../../Components/CustomLoader/CustomLoader";

function FooterList() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  const fetchCategories = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/categories/list", {
        search: search,
        page: page,
        limit: rowsPerPage
      });

      if (response?.status) {
        setCategories(response.data);
        setTotalPages(response.pagination.totalPages);
      } else {
        toast.error(response?.message || "Failed to fetch categories");
      }
    } catch (error) {
      toast.error("Error fetching categories");
      console.error("Fetch categories error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories(currentPage + 1, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchCategories(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchCategories(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/categories/delete`, {
          id
        });
        if (response.status) {
          toast.success("Category deleted successfully");
          fetchCategories(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete category");
        }
      } catch (error) {
        toast.error("Error deleting category");
        console.error("Delete category error:", error);
      }
    }
  };

  const handleStatusChange = async (category) => {
    const newStatus = category.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this category?`,
      text: `The category will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: `Yes, ${newStatus === "active" ? "activate" : "deactivate"} it!`,
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/categories/update`, {
          status: newStatus,
          id: category?._id,
        });

        if (response.status) {
          toast.success(`Category ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          fetchCategories(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || `Failed to ${newStatus === "active" ? "activate" : "deactivate"} category`);
        }
      } catch (error) {
        toast.error(`Error ${newStatus === "active" ? "activating" : "deactivating"} category`);
        console.error("Status change error:", error);
      }
    }
  };

  return (
    <div className="content-page">
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <h4 className="fs-18 fw-semibold m-0">Category Management</h4>
              <p className="fw-semibold my-2">Category List</p>
            </div>
            <div>
              <button
                className="btn btn-primary"
                onClick={() => navigate("/add-category")}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-1"></span>
                ) : (
                  <i className="mdi mdi-plus-square me-1"></i>
                )}
                Add Category
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 align-items-center">
                      <div className="col-md-12 col-lg-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <span className="input-group-text">
                            <i className="mdi mdi-magnify"></i>
                          </span>
                        </div>
                      </div>

                      <div className="col-md-12 col-lg-3 d-flex gap-2">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-1"></span>
                          ) : null}
                          Search
                        </button>

                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Category Image</th>
                            <th>Category Name</th>
                            <th>Slug</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {categories.length > 0 ? (
                            categories.map((category, index) => (
                              <tr key={category._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td> <img src={category.image} style={{ width: 50, height: 50 }} /></td>
                                <td>{category.title}</td>

                                <td>{category.slug}</td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${category.status === 'active' ? 'success' : 'danger'}`}
                                  >
                                    {category.status}
                                  </span>
                                </td>
                                <td>
                                  {new Date(category.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-category`, {
                                          state: { categoryData: category },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${category.status === 'active' ? 'warning' : 'success'}`}
                                      onClick={() => handleStatusChange(category)}
                                      title={category.status === 'active' ? 'Deactivate' : 'Activate'}
                                    >
                                      <i className={`mdi mdi-${category.status === 'active' ? 'close' : 'check'}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(category._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No categories found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FooterList;